import React, { useState, useEffect } from 'react';
import { Edit2, Save, X, Filter, ChevronDown, ChevronUp, MapPin, Globe, Search } from "lucide-react";
import apiService from '../config';
import { useLanguage } from '../StoreContext/LanguageContext';

const HighwayAdvancePayment = () => {
  const [formData, setFormData] = useState({
    serialNo: "",
    landOwnerName: "",
    gatNumber: "",
    totalArea: "",
    amount: "",
    paymentDate: "",
  });
  const [apiRecords, setApiRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [currentProjectId, setCurrentProjectId] = useState(null);
  const [currentProposalId, setCurrentProposalId] = useState(null);
  const [currentProjectName, setCurrentProjectName] = useState('');
  const [currentLaoId, setCurrentLaoId] = useState(null);
  const [records, setRecords] = useState([]);
  const [proposalDetails, setProposalDetails] = useState(null);
  const { language, translations } = useLanguage();

  // Location filter states
  const [districts, setDistricts] = useState([]);
  const [talukas, setTalukas] = useState([]);
  const [villages, setVillages] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [selectedTaluka, setSelectedTaluka] = useState('');
  const [selectedVillage, setSelectedVillage] = useState('');

  // Filter states
  const [filterMode, setFilterMode] = useState('all');
  const [showFilterPanel, setShowFilterPanel] = useState(true);

  // Loading states for dropdowns
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [loadingTalukas, setLoadingTalukas] = useState(false);
  const [loadingVillages, setLoadingVillages] = useState(false);

  const ROLES = {
    COLLECTOR: 1,
    LAO: 3,
    SAMANVAY: 6,
    PROJECT_INCHARGE: 18,
    DATA_ENTRY: 14
  };

  const userData = JSON.parse(localStorage.getItem('userData'));
  const userRole = parseInt(userData?.data?.user_role || userData?.user_role, 10);

  //Proposal ID
  useEffect(() => {
    const getProposalInfo = () => {
      const currentProposal = JSON.parse(localStorage.getItem('userData') || '{}');

      if (currentProposal) {
        try {
          const proposalId = currentProposal?.data?.default_proposal ||
            currentProposal?.data?.proposal_id ||
            currentProposal?.default_proposal ||
            currentProposal?.proposal_id ||
            null;

          const laoUserId = currentProposal?.data?.usr_id || currentProposal?.usr_id;

          setCurrentLaoId(laoUserId);
          console.log("Current LAO User ID:", laoUserId);

          return { proposalId };
        } catch (e) {
          console.error('Error parsing current project data (getProposalInfo):', e);
        }
      }
      return { proposalId: null };
    };

    const proposalInfo = getProposalInfo();
    setCurrentProposalId(proposalInfo.proposalId);
    console.log('Current Project-Proposal Info:', proposalInfo);
  }, []);

  // Get selected project from localStorage
  useEffect(() => {
    const getSelectedProject = () => {
      try {
        const storedProject = localStorage.getItem('selectedProject');
        if (storedProject) {
          const project = JSON.parse(storedProject);
          const projectIdFromStorage = project.id || project.project_id;
          const projectName = project.project_name || '';

          if (projectIdFromStorage && projectIdFromStorage !== currentProjectId) {
            setCurrentProjectId(projectIdFromStorage);
            setCurrentProjectName(projectName);
          }
        }
      } catch (error) {
        console.error('Error reading selected project:', error);
      }
    };

    getSelectedProject();
    window.addEventListener('storage', getSelectedProject);
    return () => window.removeEventListener('storage', getSelectedProject);
  }, [currentProjectId]);

  // Load districts
  const loadDistricts = async () => {
    setLoadingDistricts(true);
    try {
      const response = await apiService.getDistricts();
      const districtsData = Array.isArray(response.data) ? response.data : [];
      setDistricts(districtsData);

      // Auto-select from selectedProposal if available
      const proposalStr = localStorage.getItem('selectedProposal');
      if (proposalStr) {
        try {
          const proposal = JSON.parse(proposalStr);
          const desired = proposal.dist_name || proposal.distName;
          if (desired && !selectedDistrict) {
            const found = districtsData.find(d =>
              d.dist_name === desired || d.name === desired || d.eng_dist_name === desired
            );
            setSelectedDistrict(found ? (found.dist_name || found.id) : desired);
          }
        } catch (e) {
          console.error('Error parsing selectedProposal in loadDistricts:', e);
        }
      }
    } catch (error) {
      console.error("Failed to fetch districts:", error);
      setDistricts([]);
    } finally {
      setLoadingDistricts(false);
    }
  };

  // Load talukas
  const loadTalukas = async () => {
    if (!selectedDistrict) return;

    setLoadingTalukas(true);
    try {
      const response = await apiService.getTalukas(selectedDistrict);
      const talukasData = Array.isArray(response.data) ? response.data : [];
      setTalukas(talukasData);

      // Auto-select from selectedProposal if available
      const proposalStr = localStorage.getItem('selectedProposal');
      if (proposalStr) {
        try {
          const proposal = JSON.parse(proposalStr);
          const desiredTaluka = proposal.taluka_name || proposal.talukaName;
          if (desiredTaluka && !selectedTaluka) {
            const found = talukasData.find(t =>
              t.taluka_name === desiredTaluka || t.tehsil_name === desiredTaluka || t.eng_tehsil_name === desiredTaluka
            );
            setSelectedTaluka(found ? (found.taluka_name || found.id) : desiredTaluka);
          }
        } catch (e) {
          console.error('Error parsing selectedProposal in loadTalukas:', e);
        }
      } else {
        setSelectedTaluka('');
        setVillages([]);
        setSelectedVillage('');
      }
    } catch (error) {
      console.error("Failed to fetch talukas:", error);
      setTalukas([]);
    } finally {
      setLoadingTalukas(false);
    }
  };

  // Load villages
  const loadVillages = async () => {
    if (!selectedDistrict || !selectedTaluka) return;

    setLoadingVillages(true);
    try {
      const response = await apiService.getVillages(selectedDistrict, selectedTaluka);
      const villagesData = Array.isArray(response.data) ? response.data : [];
      setVillages(villagesData);

      // Auto-select from selectedProposal if available
      const proposalStr = localStorage.getItem('selectedProposal');
      if (proposalStr) {
        try {
          const proposal = JSON.parse(proposalStr);
          const desiredVillage = proposal.village_name || proposal.villageName;
          if (desiredVillage && !selectedVillage) {
            const found = villagesData.find(v =>
              v.village_name === desiredVillage ||
              v.Village_Name === desiredVillage ||
              v.eng_village_name === desiredVillage ||
              v.name === desiredVillage
            );
            setSelectedVillage(found ? (found.village_name || found.id || found.village_id) : desiredVillage);
          }
        } catch (e) {
          console.error('Error parsing selectedProposal in loadVillages:', e);
        }
      } else {
        setSelectedVillage('');
      }
    } catch (error) {
      console.error("Failed to fetch villages:", error);
      setVillages([]);
    } finally {
      setLoadingVillages(false);
    }
  };

  // Load initial data
  useEffect(() => {
    loadDistricts();
  }, []);

  // Load talukas when district changes
  useEffect(() => {
    loadTalukas();
  }, [selectedDistrict]);

  // Load villages when taluka changes
  useEffect(() => {
    loadVillages();
  }, [selectedDistrict, selectedTaluka]);

  // Fetch advance payment data for all locations
  const fetchAdvancePaymentsForAll = async () => {
    if (!currentProjectId || !currentProposalId) {
      setError('Project ID and Proposal ID are required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await apiService.get('/getAdvancePayment', {
        projectId: currentProjectId,
        proposalId: currentProposalId
      });

      if (response.status === 200 && response.data) {
        setApiRecords(response.data);
        setFilteredRecords(response.data);
      } else {
        setError('Failed to fetch advance payment data');
        setApiRecords([]);
        setFilteredRecords([]);
      }
    } catch (err) {
      console.error('Error fetching advance payments:', err);
      setError('Error fetching advance payment data: ' + err.message);
      setApiRecords([]);
      setFilteredRecords([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch advance payment data for specific location
  const fetchAdvancePaymentsForLocation = async () => {
    if (!currentProjectId || !currentProposalId) {
      setError('Project ID and Proposal ID are required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const params = {
        projectId: currentProjectId,
        proposalId: currentProposalId
      };

      if (selectedDistrict) params.distName = selectedDistrict;
      if (selectedTaluka) params.talukaName = selectedTaluka;
      if (selectedVillage) params.villageName = selectedVillage;

      const response = await apiService.get('/getAdvancePayment', params);

      if (response.status === 200 && response.data) {
        setApiRecords(response.data);
        setFilteredRecords(response.data);
      } else {
        // setError('Failed to fetch advance payment data');
        setApiRecords([]);
        setFilteredRecords([]);
      }
    } catch (err) {
      console.error('Error fetching advance payments:', err);
      setError('Error fetching advance payment data: ' + err.message);
      setApiRecords([]);
      setFilteredRecords([]);
    } finally {
      setLoading(false);
    }
  };

  // Load data based on filter mode
  useEffect(() => {
    if (filterMode === 'all' && currentProjectId && currentProposalId) {
      fetchAdvancePaymentsForAll();
    }
  }, [filterMode, currentProjectId, currentProposalId]);


  useEffect(() => {
    if (filterMode === 'location' && selectedDistrict && currentProjectId && currentProposalId) {
      fetchAdvancePaymentsForLocation();
    } else if (filterMode === 'location') {
      setApiRecords([]);
      setFilteredRecords([]);
    }
  }, [filterMode, selectedDistrict, selectedTaluka, selectedVillage, currentProjectId, currentProposalId]);

  // Initialize from selectedProposal
  // useEffect(() => {
  //   try {
  //     const stored = localStorage.getItem("selectedProposal");
  //     if (stored) {
  //       const proposal = JSON.parse(stored);
  //       setProposalDetails(proposal);

  //       // Set location defaults and switch to location mode
  //       const dist = proposal.dist_name || proposal.distName || '';
  //       const tal = proposal.taluka_name || proposal.talukaName || '';
  //       const vill = proposal.village_name || proposal.villageName || '';

  //       if (dist || tal || vill) {
  //         setFilterMode('location');
  //         setShowFilterPanel(true);
  //       }
  //     }
  //   } catch (e) {
  //     console.error("Error parsing selectedProposal:", e);
  //   }
  // }, []);

  useEffect(() => {
    try {
      const stored = localStorage.getItem("selectedProposal");
      if (stored) {
        const proposal = JSON.parse(stored);
        setProposalDetails(proposal);

        // keep location info but do NOT auto-switch filter
        const dist = proposal.dist_name || proposal.distName || '';
        const tal = proposal.taluka_name || proposal.talukaName || '';
        const vill = proposal.village_name || proposal.villageName || '';

        if (dist || tal || vill) {
          // preload values but stay in "all" mode
          setSelectedDistrict(dist);
          setSelectedTaluka(tal);
          setSelectedVillage(vill);
          // do NOT call setFilterMode('location') here
        }
      }
    } catch (e) {
      console.error("Error parsing selectedProposal:", e);
    }
  }, []);

  // Helper functions
  const hasValidLocationSelection = selectedDistrict;
  const canShowData = filterMode === 'all' || (filterMode === 'location' && hasValidLocationSelection);

  const getCurrentLocationDescription = () => {
    if (filterMode === 'all') {
      return 'All Locations';
    }

    if (selectedDistrict && selectedTaluka && selectedVillage) {
      return `Village: ${selectedVillage}, ${selectedTaluka}, ${selectedDistrict}`;
    } else if (selectedDistrict && selectedTaluka) {
      return `All Villages in ${selectedTaluka}, ${selectedDistrict}`;
    } else if (selectedDistrict) {
      return `All Records in ${selectedDistrict} District`;
    } else {
      return 'No location selected';
    }
  };

  // Handle location changes
  const handleDistrictChange = (e) => {
    const selectedDistrictId = e.target.value;
    setSelectedDistrict(selectedDistrictId);
    setSelectedTaluka('');
    setSelectedVillage('');
    setTalukas([]);
    setVillages([]);
    setApiRecords([]);
    setFilteredRecords([]);
  };

  const handleTalukaChange = (e) => {
    const selectedTalukaId = e.target.value;
    setSelectedTaluka(selectedTalukaId);
    setSelectedVillage('');
    setVillages([]);
    setApiRecords([]);
    setFilteredRecords([]);
  };

  const handleVillageChange = (e) => {
    const selectedVillageId = e.target.value;
    setSelectedVillage(selectedVillageId);
    setApiRecords([]);
    setFilteredRecords([]);
  };

  // Handle search functionality
  const handleSearch = (searchTerm) => {
    if (!searchTerm) {
      setFilteredRecords(apiRecords);
      return;
    }

    const filtered = apiRecords.filter(record =>
      record.owner_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.survey_no?.toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.village_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.amount?.toString().includes(searchTerm)
    );
    setFilteredRecords(filtered);
  };

  // Format date function
  const formatDateMarathi = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    const printContent = `
    <html>
    <head>
      <meta charset="UTF-8">
      <title>आगाऊ मोबदला रक्कम</title>
      <link href="https://fonts.googleapis.com/css2?family=Noto+Sans+Devanagari:wght@400;600;700&display=swap" rel="stylesheet">
      <style>
        @page {
          size: A4 portrait;
          margin-top: 0.19in;
          margin-bottom: 0.25in;
          margin-left: 0.81in;
          margin-right: 0.5in;
        }
        
        body {
          font-family: 'DVOT-Surekh', 'Mangal', 'Arial', sans-serif;
          font-size: 12pt;
          line-height: 1.4;
        }
        
        * {
          box-sizing: border-box;
        }
        
        /* Headings */
        h1, h2, h3, h4, h5, h6 {
          margin: 1rem 0 0.5rem 0;
          page-break-after: avoid;
          font-weight: bold;
        }
        
        h2 {
          font-size: 16pt;
          text-align: center;
          margin-top: 1rem;
          margin-bottom: 1rem;
          font-family: 'DVOT-Surekh', 'Mangal', sans-serif;
        }
        
        h3 {
          text-align: center;
          margin: 0;
          font-size: 14pt;
        }
        
        /* Paragraphs */
        p {
          margin: 0.5rem 0;
          line-height: 1.6;
          orphans: 3;
          widows: 3;
          font-size: 11pt;
        }
        
        /* Table styles */
        table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 10px;
          margin-bottom: 1rem;
          page-break-inside: auto !important;
          font-size: 10pt;
        }
        
        thead {
          display: table-header-group;
        }
        
        tr {
          page-break-inside: avoid;
          page-break-after: auto;
        }
        
        th, td {
          border: 1px solid #000;
          padding: 4px;
          text-align: center;
          font-size: 9pt;
          vertical-align: middle;
          page-break-inside: avoid;
        }
        
        th {
          background-color: #f0f0f0;
          font-weight: bold;
          font-size: 10pt;
        }
        
        /* Text utilities */
        .header-string {
          font-size: 11pt;
          margin: 10px 0;
          text-align: center;
        }
        
        .text-center {
          text-align: center;
        }
        
        .text-right {
          text-align: right;
        }
        
        .text-left {
          text-align: left;
        }
        
        .text-base {
          font-size: 11pt;
        }
        
        .text-sm {
          font-size: 10pt;
        }
        
        .text-xs {
          font-size: 9pt;
        }
        
        .text-lg {
          font-size: 1.125rem;
        }
        
        .text-xl {
          font-size: 1.5rem;
        }
        
        .text-2xl {
          font-size: 1.8rem;
        }
        
        /* Font weights */
        .font-bold {
          font-weight: bold;
        }
        
        .font-semibold {
          font-weight: 600;
        }
        
        .font-medium {
          font-weight: 500;
        }
        
        /* Margins */
        .mb-1 {
          margin-bottom: 0.25rem;
        }
        
        .mb-2 {
          margin-bottom: 0.5rem;
        }
        
        .mb-4 {
          margin-bottom: 1rem;
        }
        
        .mb-6 {
          margin-bottom: 1.5rem;
        }
        
        .mt-1 {
          margin-top: 0.25rem;
        }
        
        .mt-2 {
          margin-top: 0.5rem;
        }
        
        .mt-4 {
          margin-top: 1rem;
        }
        
        .mt-6 {
          margin-top: 1.5rem;
        }
        
        .mt-8 {
          margin-top: 2rem;
        }
        
        .mt-10 {
          margin-top: 2rem;
        }
        
        /* Padding */
        .p-2 {
          padding: 0.5rem;
        }
        
        .p-3 {
          padding: 0.75rem;
        }
        
        .p-4 {
          padding: 1rem;
        }
        
        /* Borders */
        .border {
          border: 1px solid black;
        }
        
        .border-2 {
          border: 2px solid black;
        }
        
        .border-black {
          border-color: black;
        }
        
        /* Layout */
        .flex {
          display: flex;
        }
        
        .justify-between {
          justify-content: space-between;
        }
        
        .justify-center {
          justify-content: center;
        }
        
        .items-center {
          align-items: center;
        }
        
        /* Spacing utilities */
        .space-y-4 > * + * {
          margin-top: 1rem;
        }
        
        .mx-auto {
          margin-left: auto;
          margin-right: auto;
        }
        
        /* Other utilities */
        .underline {
          text-decoration: underline;
        }
        
        .indent-8 {
          text-indent: 2rem;
        }
        
        br {
          line-height: 1.2;
        }
        
        /* Print specific */
        @media print {
          .no-print {
            display: none !important;
          }
          
          body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          
          .page-break {
            page-break-after: always;
          }
          
          table {
            page-break-inside: auto !important;
            font-size: 10px;
          }
          
          thead {
            display: table-header-group;
          }
          
          tfoot {
            display: table-footer-group;
          }
          
          tr {
            page-break-inside: avoid;
            page-break-after: auto;
          }
          
          th, td {
            page-break-inside: avoid;
            page-break-after: auto;
            padding: 2px;
          }
          
          .page-break-avoid {
            page-break-inside: avoid;
            break-inside: avoid;
          }
        }
      </style>
    </head>
    <body>
    <h1 class="text-center font-bold text-lg">${currentProjectName}</h1>
    <h1 class="text-center font-bold text-base">भूसंपादन प्रकरण क्रमांक ${proposalDetails?.section_11_bhusampadan_no || ''}</h1>
      <h2 style="text-align: center;">${translations[language]?.AdAdvanceAmount || "अग्रिमाची रक्कम"}</h2>
      <table>
        <thead>
          <tr>
            <th>${translations[language]?.AdSrNo || "अ.क्र."}</th>
            <th>${translations[language]?.AdLandholderName || "भूधारकाचे नाव"}</th>
            <th>${translations[language]?.AdSurveyNumber || "गट/सर्व्हे क्रमांक"}</th>
            <th>${translations[language]?.AdVillage || "गाव"}</th>
            <th>${translations[language]?.AdTotalArea || "एकूण क्षेत्र (हे.आर.)"}</th>
            <th>${translations[language]?.AdAdvanceAmount || "अग्रिमाची रक्कम"}</th>
            <th>${translations[language]?.AdSanctionDate || "प्रदानाचा दिनांक"}</th>
            <th>${translations[language]?.No || "मंजुरी आदेश क्रमांक"}</th>
            <th>${translations[language]?.AdDate || "मंजुरी आदेश दिनांक"}</th>
          </tr>
        </thead>
        <tbody>
          ${filteredRecords.map((record, index) => `
            <tr>
              <td>${index + 1}</td>
              <td>${record.owner_name}</td>
              <td>${record.survey_no}</td>
              <td>${record.village_name}</td>
              <td>${record.owner_total_area || "-"}</td>
              <td>${record.amount ? `₹${record.amount}` : '-'}</td>
              <td>${record.date ? formatDate(record.date) : '-'}</td>
              <td>${record.approval_no || '-'}</td>
              <td>${record.approval_date ? formatDate(record.approval_date) : '-'}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    </body>
    </html>
  `;
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  // Start editing
  const startEdit = (record) => {
    setEditingId(record.land_owner_id);
    setEditData({
      amount: record.amount || '',
      date: record.date ? new Date(record.date).toISOString().split('T')[0] : ''
    });
  };

  // Cancel editing
  const cancelEdit = () => {
    setEditingId(null);
    setEditData({});
  };

  // Handle edit input change
  const handleEditChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Save edited record
  const saveEdit = async (record) => {
    if (!editData.amount || !editData.date) {
      alert('कृपया रक्कम आणि दिनांक भरा');
      return;
    }

    try {
      setLoading(true);

      const updateData = {
        project_id: currentProjectId,
        proposal_id: currentProposalId,
        owner_id: record.land_owner_id,
        amount: editData.amount,
        date: editData.date,
        approval_no: editData.approval_no,
        approval_date: editData.approval_date,
      };

      // Use the single API endpoint for both create and update
      await apiService.post('/postAddUpdateAdvancePayment', updateData);

      // Refresh data after successful update
      if (filterMode === 'all') {
        await fetchAdvancePaymentsForAll();
      } else {
        await fetchAdvancePaymentsForLocation();
      }

      setEditingId(null);
      setEditData({});

      alert('✅ डेटा यशस्वीपणे अपडेट झाला');
    } catch (err) {
      console.error('Error updating record:', err);
      alert('❌ डेटा अपडेट करतांना त्रुटी: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
        <div className="flex justify-center items-center h-80">
          <div className="text-center">
            <div className="inline-flex relative">
              <div className="w-16 h-16 rounded-full border-4 border-slate-700 border-t-transparent animate-spin"></div>
              <div className="w-16 h-16 rounded-full border-4 border-purple-500 border-b-transparent border-r-transparent animate-spin absolute inset-0" style={{ animationDuration: '1.5s', animationDirection: 'reverse' }}></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <MapPin className="h-6 w-6 text-purple-400 animate-pulse" />
              </div>
            </div>
            <p className="text-gray-400 mt-4">डेटा लोड होत आहे...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className="max-w-7xl mx-auto">

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {/* Search and Actions Bar */}
        {canShowData && (
          <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">

              {/* Action Buttons */}
              <div className="flex gap-3">

                {/* Filter Button */}
                <button
                  onClick={() => setShowFilterPanel(!showFilterPanel)}
                  className="flex items-center space-x-2 px-4 py-2.5 bg-orange-600 text-white rounded-lg font-medium transition-all duration-200 shadow-sm hover:shadow-md hover:bg-orange-700"
                >
                  <Filter className="h-4 w-4" />
                  <span>{translations[language]?.Filters || 'Filters'}</span>
                  {showFilterPanel ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </button>

                {/* Print Button */}
                <button
                  onClick={handlePrint}
                  disabled={filteredRecords.length === 0}
                  className="flex items-center px-6 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 shadow-lg transition-all duration-200 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {translations[language]?.Print || 'Print'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Filter Panel */}
        {showFilterPanel && (
          <div className="bg-white rounded-2xl shadow-xl p-6 mb-6 border border-gray-200">
            <div className="space-y-6">

              {/* Filter Mode Selection */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-1 bg-gray-100 rounded-xl p-1.5 shadow-inner">
                  <button
                    onClick={() => setFilterMode('location')}
                    className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 flex items-center space-x-2 ${filterMode === 'location'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                      }`}
                  >
                    <MapPin className="h-4 w-4" />
                    <span>{translations[language]?.FilterByLocation || 'Filter by Location'}</span>
                  </button>
                  <button
                    onClick={() => setFilterMode('all')}
                    className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 flex items-center space-x-2 ${filterMode === 'all'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                      }`}
                  >
                    <Globe className="h-4 w-4" />
                    <span>{translations[language]?.AllRecords || 'All Records'}</span>
                  </button>
                </div>
              </div>

              {/* Location Selection */}
              {filterMode === 'location' && (
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                  <div className="space-y-4">

                    {/* Header */}
                    <div className="flex items-center">
                      <MapPin className="h-5 w-5 text-blue-600 mr-2" />
                      <h3 className="text-lg font-semibold text-gray-800">{translations[language]?.SelectLocation || 'Select Location'}</h3>
                    </div>

                    {/* Location Dropdowns */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

                      {/* District Dropdown */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {translations[language]?.district || 'District'} <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={selectedDistrict}
                          onChange={handleDistrictChange}
                          disabled={loadingDistricts}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed transition-all duration-200 shadow-sm"
                        >
                          <option value="">
                            {loadingDistricts ? 'Loading Districts...' : 'Select District'}
                          </option>
                          {districts.map((district) => (
                            <option key={district.dist_name} value={district.dist_name}>
                              {district.dist_name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Taluka Dropdown */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {translations[language]?.taluka || 'Taluka'} <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={selectedTaluka}
                          onChange={handleTalukaChange}
                          disabled={!selectedDistrict || loadingTalukas}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed transition-all duration-200 shadow-sm"
                        >
                          <option value="">
                            {loadingTalukas ? 'Loading Talukas...' : 'Select Taluka'}
                          </option>
                          {talukas.map((taluka) => (
                            <option key={taluka.taluka_name} value={taluka.taluka_name}>
                              {taluka.taluka_name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Village Dropdown */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {translations[language]?.village || 'Village'} <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={selectedVillage}
                          onChange={handleVillageChange}
                          disabled={!selectedTaluka || loadingVillages}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed transition-all duration-200 shadow-sm"
                        >
                          <option value="">
                            {loadingVillages ? 'Loading Villages...' : 'Select Village'}
                          </option>
                          {villages.map((village) => {
                            const villageId = village.village_name;
                            const villageName = village.village_name;
                            return (
                              <option key={villageId} value={villageId}>
                                {villageName}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                    </div>

                    {/* Status Messages */}
                    {filterMode === 'location' && !hasValidLocationSelection && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                        <p className="text-sm text-amber-700">
                          {translations[language]?.PleaseSelectAll || 'Please select at least district to view data.'}
                        </p>
                      </div>
                    )}

                    {filterMode === 'location' && hasValidLocationSelection && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                        <p className="text-sm text-green-700 flex items-center">
                          <span className="mr-2">✓</span>
                          {translations[language]?.ShowingDataFor || 'Showing data for'}: {getCurrentLocationDescription()}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Data Table */}
        {canShowData && filteredRecords.length > 0 && (
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-gray-300 text-sm">
                <thead>
                  <tr className="bg-gray-200">
                    {userRole === ROLES.LAO && (
                      <th className="border p-2">{translations[language]?.AdAction}</th>
                    )}
                    <th className="border p-2">{translations[language]?.AdSrNo}</th>
                    <th className="border p-2">{translations[language]?.AdLandholderName}</th>
                    <th className="border p-2">{translations[language]?.AdSurveyNumber}</th>
                    <th className="border p-2">{translations[language]?.AdVillage}</th>
                    <th className="border p-2">{translations[language]?.AdTotalArea}</th>
                    <th className="border p-2">{translations[language]?.AdAdvanceAmount}</th>
                    <th className="border p-2">{translations[language]?.AdSanctionDate}</th>
                    <th className="border p-2">{translations[language]?.No}</th>
                    <th className="border p-2">{translations[language]?.AdApprovalDate}</th>
                  </tr>
                </thead>
                <tbody>

                  {filteredRecords.map((record, index) => (
                    <tr key={record.land_owner_id} className="hover:bg-gray-50">
                      {/* {userRole === ROLES.LAO && ( */}
                      {/* Data Entry */}
                      {(userRole === ROLES.LAO || userRole === ROLES.DATA_ENTRY) && (
                        <td className="border p-2 text-center">
                          {/* क्रिया */}
                          {editingId === record.land_owner_id ? (
                            <div className="flex gap-2 justify-center">
                              <button
                                onClick={() => saveEdit(record)}
                                disabled={loading}
                                className="bg-green-500 text-white p-1 rounded hover:bg-green-600 disabled:opacity-50"
                                title="सेव करा"
                              >
                                <Save size={16} />
                              </button>
                              <button
                                onClick={cancelEdit}
                                className="bg-red-500 text-white p-1 rounded hover:bg-red-600"
                                title="रद्द करा"
                              >
                                <X size={16} />
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => startEdit(record)}
                              className="bg-blue-500 text-white p-1 rounded hover:bg-blue-600"
                              title="संपादित करा"
                            >
                              <Edit2 size={16} />
                            </button>
                          )}
                        </td>
                      )}
                      <td className="border p-2 text-center">{index + 1}</td>
                      {/* भूधारकाचे नाव	 */}
                      <td className="border p-2">{record.owner_name}</td>
                      {/* गट/सर्व्हे क्रमांक	 */}
                      <td className="border p-2 text-center">{record.survey_no}</td>
                      {/* गाव	 */}
                      <td className="border p-2">{record.village_name}</td>
                      {/* एकूण क्षेत्र (हे.आर.)	 */}
                      <td className="border p-2 text-center">{record.owner_total_area || "-"} </td>
                      {/* अग्रिमाची रक्कम	 */}
                      <td className="border p-2 text-right">
                        {editingId === record.land_owner_id ? (
                          <input
                            type="number"
                            value={editData.amount}
                            onChange={(e) => handleEditChange('amount', e.target.value)}
                            className="w-full p-1 border rounded text-right"
                            placeholder="रक्कम"
                          />
                        ) : (
                          record.amount ? `₹${record.amount}` : '-'
                        )}
                      </td>
                      {/* प्रदानाचा दिनांक	 */}
                      <td className="border p-2 text-center">
                        {editingId === record.land_owner_id ? (
                          <input
                            type="date"
                            value={editData.date}
                            onChange={(e) => handleEditChange('date', e.target.value)}
                            className="w-full p-1 border rounded"
                          />
                        ) : (
                          record.date ? formatDate(record.date) : '-'
                        )}
                      </td>

                      {/* तपशील	 */}
                      <td className="border p-2 text-center">
                        {editingId === record.land_owner_id ? (
                          <input
                            type="string"
                            value={editData.approval_no}
                            onChange={(e) => handleEditChange('approval_no', e.target.value)}
                            className="w-full p-1 border rounded"
                          />
                        ) : (
                          record.approval_no ? record.approval_no : '-'
                        )}
                      </td>
                      {/* मंजुरी आदेश दिनांक	 */}
                      <td className="border p-2 text-center">
                        {editingId === record.land_owner_id ? (
                          <input
                            type="date"
                            value={editData.approval_date}
                            onChange={(e) => handleEditChange('approval_date', e.target.value)}
                            className="w-full p-1 border rounded"
                          />
                        ) : (
                          record.approval_date ? formatDate(record.approval_date) : '-'
                        )}
                      </td>

                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* No Data Message */}
        {canShowData && filteredRecords.length === 0 && !loading && (
          <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
            <div className="text-gray-400 mb-4">
              <MapPin className="h-16 w-16 mx-auto" />
            </div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">{translations[language]?.NoRecordsFound || 'No Records Found'}</h3>
            <p className="text-gray-600">कोणताही डेटा उपलब्ध नाही</p>
          </div>
        )}

        {/* Location Selection Required Message */}
        {filterMode === 'location' && !hasValidLocationSelection && (
          <div className="mt-6 text-center py-12">
            <div className="max-w-md mx-auto">
              <MapPin className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg font-medium mb-2">{translations[language]?.LocationSelectionRequired || 'Location Selection Required'}</p>
              <p className="text-gray-400 text-sm">{translations[language]?.PleaseSelectFromFilter || 'Please select at least district from the filter panel above to view data.'}</p>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default HighwayAdvancePayment;
